
<template>
	<CEntryViewTabs
		v-if="globalStore.currentView"
		:tabs="tabs"
		:selected-view-id="selectedViewId"
		:global-store="globalStore"
		:view-id="globalStore.currentView?.Id"
		:view-name="globalStore.currentView?.Name"
		:view-type="globalStore.currentView?.Type"
		:current-view-entry-count="currentViewEntryCount"
		:is-task-view="globalStore.currentView?.IsTaskView"
		:can-edit-views="canEditViews"
		:show-workflow-tasks="showWorkflowTasks"
		:views="visibleViews"
		:use-mobile-layout="useMobileLayout"
		:is-org-owner="isOrgOwner"
		:form-data="formData"
		:form-name="formName"
		:form-id="formId"
		@change-view="$emit('change-view', $event)"
		@new-view="$emit('new-view', $event)"
		@reorder-view="$emit('reorder-view', $event)"
		@save-view="$emit('save-view', $event)"
		@delete-view="$emit('delete-view', $event)"
	/>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { defineComponent, computed, inject } from 'vue';
	import CEntryViewTabs, { type EntryViewTab } from './EntryViewTabs.vue';
	import { globalStore } from 'src/stores/global-store';
	import { ViewType } from '@cognitoforms/types/server-types/forms/model/view-type';
	import type { MenuViewData } from 'src/views/entries/EntryViewsMenu.vue';
	import { isMobileKey } from 'src/utilities/injection-keys';
	import type { EntryViewsFormData } from '@cognitoforms/types/server-types/forms/model/entry-views-form-data';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import { getEntryViews } from 'src/composables/get-entry-views';

	export default defineComponent({
		name: 'CEntryViewTabsContainer',
		components: {
			CEntryViewTabs
		},
		props: {
			views: {
				type: Array as PropType<MenuViewData[]>,
				default: null
			},
			canEditViews: {
				type: Boolean,
				default: false
			},
			showWorkflowTasks: {
				type: Boolean,
				default: false
			},
			currentViewEntryCount: {
				type: Number as PropType<number | null>,
				default: 0
			},
			isOrgOwner: {
				type: Boolean,
				default: false
			},
			formData: {
				type: Object as PropType<EntryViewsFormData>,
				required: true
			},
			formName: {
				type: String,
				required: true
			},
			formId: {
				type: String,
				default: null
			}
		},
		emits: ['change-view', 'new-view', 'reorder-view', 'save-view', 'delete-view'],
		setup(props) {
			const iconMap: Record<ViewType, string> = {
				[ViewType.Form]: 'form-view',
				[ViewType.Table]: 'grid'
			};
			const getIcon = (id: string) => iconMap[id];
			const hideSavedViews = computed(() => !globalStore.organization.Plan.IsPaidPlan);
			const selectedViewId = computed(() => globalStore.currentView.Id);
			const tabs = computed<EntryViewTab[]>(() => {
				const entryViews = getEntryViews();
				const isGuest = globalStore.organization.Role === ClientRole.Guest;

				return entryViews
					.filter(v => !(isGuest && v.Type === ViewType.Form))
					.map(view => {
						const entryCount = selectedViewId.value === view.Id && props.currentViewEntryCount !== null ? props.currentViewEntryCount : view.Entries;
						const isLocked = hideSavedViews.value && !view.Id.endsWith('-0');
						return {
							name: view.Name,
							value: view.Id,
							icon: isLocked ? 'lock' : (view.IsTaskView ? 'task-grid' : getIcon(view.Type)),
							badge: view.Type !== 'Form' ? {
								text: entryCount,
								type: view.IsTaskView && entryCount > 0 ? 'alert' : 'alt',
								isLoading: !view.Valid
							} : null,
							isLocked: isLocked
						};
					});
			});

			const visibleViews = computed<MenuViewData[]>(() => {
				const viewNodes: MenuViewData[] = [];
				props.views.forEach(view => {
					viewNodes.push({
						...view,
						IsLocked: hideSavedViews.value && !view.Id.endsWith('-0')
					});
				});

				return viewNodes;
			});

			return {
				tabs,
				selectedViewId,
				globalStore,
				useMobileLayout: inject(isMobileKey),
				visibleViews
			};
		}
	});
</script>